import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { Icon } from '../ui/icons';
import PinPad from '../components/PinPad';
import UserDeactivatedPopup from '../components/popups/UserDeactivatedPopup';
import { PIN_LENGTH, PIN_LOCKOUT, verifyPropertyCredentials } from '../services/StaffAuthService';
import './StaffLock.css';

/**
 * The screen the device rests on all service.
 *
 * There is no list of faces to pick from: a PIN identifies one person, so typing
 * it is the whole interaction. That only holds if PINs are unique within the
 * property — the API has to guarantee it (see StaffAuthService).
 *
 * The property session is already established by the time anyone gets here, so
 * signing in never asks for an email or a password. Signing the *device* out of
 * the venue does, because it is a different act by a different person: it ends
 * the property session for everyone, so knowing a 4-digit PIN must not be enough.
 */
const StaffLock: React.FC = () => {
    const navigate = useNavigate();
    const { pinLogin, logout, lock, staff } = useAuth();

    const [pin, setPin] = useState('');
    const [error, setError] = useState('');
    const [errorAt, setErrorAt] = useState(0);
    const [busy, setBusy] = useState(false);
    const [signingIn, setSigningIn] = useState(false);
    const [attempts, setAttempts] = useState(0);
    const [cooldownUntil, setCooldownUntil] = useState(0);
    const [now, setNow] = useState(Date.now());
    const [showDeactivated, setShowDeactivated] = useState(false);
    const [propertyName, setPropertyName] = useState('');

    // Signing the device out of the venue, gated behind the venue's own credentials.
    const [exiting, setExiting] = useState(false);
    const [exitEmail, setExitEmail] = useState('');
    const [exitPassword, setExitPassword] = useState('');
    const [exitError, setExitError] = useState('');
    const [exitBusy, setExitBusy] = useState(false);

    const enterTimer = useRef<number | undefined>(undefined);
    /** Set for the duration of a PIN check, so the guard below ignores the session
      * it is in the middle of creating. A ref because it has to be true before the
      * next render, not after it. */
    const authenticating = useRef(false);

    useEffect(() => {
        if (localStorage.getItem('displayDeactivatedUser') === 'true') {
            setShowDeactivated(true);
            localStorage.removeItem('displayDeactivatedUser');
        }
        const stored = localStorage.getItem('currentProperty');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                setPropertyName(parsed?.displayName || parsed?.name || '');
            } catch {
                /* no property name is fine — the screen still works */
            }
        }
    }, []);

    // Only ticks while a cooldown is running, so the screen is otherwise idle.
    const cooling = cooldownUntil > now;
    useEffect(() => {
        if (cooldownUntil <= Date.now()) return;
        const id = window.setInterval(() => setNow(Date.now()), 500);
        return () => window.clearInterval(id);
    }, [cooldownUntil]);

    useEffect(() => () => window.clearTimeout(enterTimer.current), []);

    /**
     * A locked screen must actually mean locked. Arriving here by any route other
     * than the Lock button — a typed URL, a stale tab — would otherwise leave the
     * previous person's session alive behind a screen that says it is not.
     */
    useEffect(() => {
        if (staff && !authenticating.current) lock();
    }, [staff, lock]);

    /** Typing again is the correction — the previous complaint stops applying. */
    const changePin = (next: string) => {
        setPin(next);
        if (error) setError('');
    };

    const fail = (message: string) => {
        setError(message);
        setErrorAt(Date.now());
        setPin('');
    };

    const submit = async (entered: string) => {
        if (busy || cooling) return;
        setBusy(true);
        setError('');
        authenticating.current = true;

        const result = await pinLogin(entered);
        setBusy(false);

        if (result.ok) {
            setAttempts(0);
            setSigningIn(true);
            enterTimer.current = window.setTimeout(() => navigate('/tables', { replace: true }), 500);
            return;
        }

        authenticating.current = false;
        const next = attempts + 1;
        setAttempts(next);
        if (next >= PIN_LOCKOUT.attempts) {
            setCooldownUntil(Date.now() + PIN_LOCKOUT.cooldownMs);
            setNow(Date.now());
            setAttempts(0);
            fail('Too many attempts — wait a moment before trying again');
        } else {
            fail(result.message);
        }
    };

    const confirmExit = async () => {
        if (exitBusy) return;
        if (!exitEmail.trim() || !exitPassword) {
            setExitError('Enter the venue email and password');
            return;
        }
        setExitBusy(true);
        setExitError('');

        const result = await verifyPropertyCredentials(exitEmail.trim(), exitPassword);
        if (!result.ok) {
            setExitBusy(false);
            setExitError(result.message);
            return;
        }

        await logout();
        localStorage.clear();
        navigate('/login', { replace: true });
    };

    if (signingIn) {
        return (
            <div className='stafflock ps-scope'>
                <div className='stafflock__loading'>
                    <span className='stafflock__spinner' />
                    <p className='stafflock__loadingtext'>Signing you in…</p>
                </div>
            </div>
        );
    }

    const secondsLeft = Math.ceil((cooldownUntil - now) / 1000);

    const subtitle = propertyName
        ? `${propertyName} · tap in to start working`
        : 'Tap in to start working';

    let status: string = '';
    let statusTone: 'muted' | 'error' = 'muted';
    if (busy) {
        status = 'Checking…';
    } else if (cooling) {
        status = `Too many attempts — try again in ${secondsLeft}s`;
        statusTone = 'error';
    } else if (error) {
        status = error;
        statusTone = 'error';
    }

    return (
        <div className='stafflock ps-scope'>
            {exiting ? (
                <div className='stafflock__card stafflock__card--exit'>
                    <header className='stafflock__head'>
                        <h1 className='stafflock__title'>Sign this device out</h1>
                        <p className='stafflock__note'>
                            This ends the session for everyone on this device. Enter the venue
                            email and password to confirm.
                        </p>
                    </header>

                    <div className='stafflock__form'>
                        <label className='stafflock__label' htmlFor='exit-email'>Venue email</label>
                        <input
                            id='exit-email'
                            className='stafflock__input'
                            type='email'
                            autoComplete='username'
                            value={exitEmail}
                            onChange={e => { setExitEmail(e.target.value); setExitError(''); }}
                        />

                        <label className='stafflock__label' htmlFor='exit-password'>Password</label>
                        <input
                            id='exit-password'
                            className='stafflock__input'
                            type='password'
                            autoComplete='current-password'
                            value={exitPassword}
                            onChange={e => { setExitPassword(e.target.value); setExitError(''); }}
                            onKeyDown={e => { if (e.key === 'Enter') confirmExit(); }}
                        />

                        {exitError && <span className='stafflock__error'>{exitError}</span>}
                    </div>

                    <div className='stafflock__actions'>
                        <button
                            type='button'
                            className='stafflock__btn'
                            onClick={() => {
                                setExiting(false);
                                setExitEmail('');
                                setExitPassword('');
                                setExitError('');
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type='button'
                            className='stafflock__btn stafflock__btn--danger'
                            onClick={confirmExit}
                            disabled={exitBusy}
                        >
                            {exitBusy ? 'Checking…' : 'Sign out'}
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <div className='stafflock__card'>
                        <header className='stafflock__head'>
                            <h1 className='stafflock__title'>Enter your PIN</h1>
                            {/* One line, always here. When there is something to say it
                              * says that instead of the greeting, so a message costs no
                              * space and moves nothing. */}
                            <p
                                className={`stafflock__note${statusTone === 'error' ? ' is-error' : ''}`}
                                role='status'
                                aria-live='polite'
                            >
                                {status || subtitle}
                            </p>
                        </header>

                        <PinPad
                            value={pin}
                            onChange={changePin}
                            onComplete={submit}
                            length={PIN_LENGTH}
                            disabled={busy || cooling}
                            errorAt={errorAt}
                        />
                    </div>

                    <button type='button' className='stafflock__exit' onClick={() => setExiting(true)}>
                        <Icon.LogOut size={15} />
                        <span>Sign this device out of the venue</span>
                    </button>
                </>
            )}

            {showDeactivated && (
                <>
                    <div className='popup-overlay' />
                    <UserDeactivatedPopup onClose={() => setShowDeactivated(false)} />
                </>
            )}
        </div>
    );
};

export default StaffLock;
